'use client'

import { useEffect, useSyncExternalStore } from 'react'
import { z } from 'zod'

const key = 'inb_recent'
const RecentSchema = z.object({
  views: z.array(z.string()),
  keywords: z.array(z.string()),
})

class RecentView {
  private listeners: Array<() => void> = []

  public data: z.infer<typeof RecentSchema> = {
    views: [],
    keywords: [],
  }

  constructor(
    private opts: {
      maxViews: number
      maxKeywords: number
    },
  ) {
    this.sync(true)
  }

  private sync(skipSave = false) {
    if (typeof window === 'undefined') return

    if (!skipSave) {
      localStorage.setItem(key, JSON.stringify(this.data))

      this.listeners.forEach((listener) => listener())
    }

    const data = localStorage.getItem(key) ?? null
    if (data) {
      const result = RecentSchema.safeParse(JSON.parse(data))
      if (result.success) {
        this.data = result.data
      }
    }
  }

  public addCharacterView(characterId: string) {
    this.data.views = [...new Set([characterId, ...this.data.views])].slice(
      0,
      this.opts.maxViews,
    )

    this.sync()
  }

  public addKeyword(keyword: string) {
    this.data.keywords = [...new Set([keyword, ...this.data.keywords])].slice(
      0,
      this.opts.maxKeywords,
    )

    this.sync()
  }

  public deleteKeyword(keyword: string) {
    this.data.keywords = this.data.keywords.filter((k) => k !== keyword)

    this.sync()
  }

  public subscribe(listener: () => void) {
    this.listeners.push(listener)

    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener)
    }
  }
}

export const recentView = new RecentView({
  maxViews: 15,
  maxKeywords: 10,
})

export function useRecent() {
  return useSyncExternalStore(
    recentView.subscribe.bind(recentView),
    () => recentView.data,
  )
}

export function RecordRecentCharacterView({
  characterId,
}: {
  characterId: string
}) {
  useEffect(() => {
    recentView.addCharacterView(characterId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return null
}
